/**
 * Voucher stay-cost helpers (Dennis 2026-06-02).
 *
 * A hotel room is priced PER ROOM for its occupancy. The calendar grid and the
 * realtime quote both return a per-person price computed as `room / adults`
 * (see backend calendar.service.ts:519 `hotelPricePerPerson = hotelWithMargin /
 * adults`). So the stay's HOTEL cost = perPax * adults. Multiplying instead by
 * the full party (adults + children) double-charges the children on the room.
 *
 * For a 2 adults + 2 children family voucher (deal 292, €999) that pushed the
 * cheapest week (room ~€518) to a computed €1.036, i.e. €37 OVER the voucher,
 * so no date ever showed the deserved "€ 0,-" and the customer was over-billed.
 *
 * 2026-09-22 (Jeanne, Kartik #sitereview 49, deal 285 ING-Phantasialand):
 * the ACTIVITY-inclusive branch had the same per-room error. The calendar
 * per-pax for an activity package is `hotel/adults + ticketPerPax`, so
 * multiplying THAT blend by the full party charges the ROOM once per child as
 * well. Correct shape: the hotel component times adults, the ticket component
 * times the full party. On deal 285 (2A+2C, voucher EUR 239) the cheapest date
 * went from a EUR 117 surcharge (EUR 30/pax on the tile) to EUR 3,40 for the
 * whole party. Both branches now multiply the HOTEL by adults, which is why
 * `hotelChargeablePax` no longer needs the `hasActivity` distinction; the
 * ticket side is `ticketChargeablePax` and `voucherStayTotal` combines them.
 *
 * The front end only has the BLENDED per-pax, so it recovers the hotel part by
 * SUBTRACTING the ticket per-pax (`activityPrice` in the /api/booking/calendar
 * entry). That approximation is deliberate and temporary: step 2 of Jeanne's
 * decision has the backend return the two components separately.
 */

/** Conservative "does this deal include an activity?" check. Defaults to
 *  treating the deal as activity-bearing unless we are sure it is hotel-only,
 *  so we never under-charge by mistreating an activity deal as hotel-only. */
export function dealHasActivity(deal, extraSignals = {}) {
  if (!deal) return true; // unknown -> safe (keep full-party multiplier)
  if (
    deal.has_activity === true ||
    deal.has_activity === 1 ||
    deal.has_activity === "1" ||
    deal.has_activity === "true"
  )
    return true;
  const hasItems = (raw) => {
    if (Array.isArray(raw)) return raw.length > 0;
    if (typeof raw === "string") {
      const trimmed = raw.trim();
      if (!trimmed) return false;
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.length > 0;
        if (parsed && typeof parsed === "object") {
          return Object.keys(parsed).length > 0;
        }
        return Boolean(parsed);
      } catch {
        // A non-empty legacy label is evidence of an activity; only a parsed
        // empty collection may suppress the ticket/cost signal.
        return true;
      }
    }
    return !!(raw && typeof raw === "object" && Object.keys(raw).length > 0);
  };
  if (
    hasItems(deal.activities) ||
    hasItems(deal.activities_detail) ||
    hasItems(deal.optional_activities)
  )
    return true;
  // Live booking-store signals (a date-specific activity price > 0).
  if (Number(extraSignals.activityPricing) > 0) return true;
  if (Number(extraSignals.selectedDateActivityPrice) > 0) return true;
  return false;
}

/**
 * The activity list `buildPricingInput` (src/hooks/useBookingQuote.js) reads
 * the `/pricing/quote` activity from: `activities_detail` when it has items,
 * else `activities`. One expression for the quote AND every display surface,
 * so they can never disagree about whether a ticket travels with the quote.
 */
export function quoteActivityList(deal) {
  return (deal?.activities_detail?.length > 0 ? deal.activities_detail : deal?.activities) || [];
}

/**
 * Does the `/pricing/quote` payload carry an activity the backend prices per
 * guest? True only for a real array whose first entry is an object (that
 * entry becomes the payload's `activity`).
 *
 * Codex REQUEST_CHANGES on #1442 (fe4ff9e2, 24-09-2026): the hotel/ticket
 * split (hotel x adults, ticket subtracted from the per-pax) is only sound
 * when the backend adds the tickets back from that `activity`. A deal flagged
 * `has_activity: true` with `activities: []` sends `activity: null`; splitting
 * there dropped every ticket from the quote (2+2, 95,45 p.p., ticket 32,20:
 * quote 126,50 instead of at least 381,80). Without an activity in the payload
 * every surface keeps the old conservative shape: the whole party times the
 * blended per-pax, nothing subtracted.
 */
export function quoteSendsActivity(deal) {
  const list = quoteActivityList(deal);
  return (
    Array.isArray(list) &&
    list.length > 0 &&
    list[0] !== null &&
    typeof list[0] === "object"
  );
}

/**
 * How many pax the per-ROOM hotel price must be multiplied by.
 *
 * Always `adults`: the room is priced per room (perPax = room / adults) for
 * hotel-only AND for activity packages. Children ride along in the room, they
 * are charged on the TICKET side (see {@link ticketChargeablePax}).
 *
 * `hasActivity` is accepted and ignored so the four call surfaces can keep
 * passing one options object; it no longer changes the hotel multiplier.
 */
export function hotelChargeablePax({ adults }) {
  return Math.max(1, Number(adults) || 0);
}

/**
 * How many pax the per-person ACTIVITY ticket must be multiplied by.
 * Full party on an activity package (every traveller needs a ticket), 0 on a
 * hotel-only deal (there is no ticket component in the per-pax price).
 */
export function ticketChargeablePax({ adults, children, hasActivity }) {
  if (!hasActivity) return 0;
  const a = Math.max(1, Number(adults) || 0);
  const c = Math.max(0, Number(children) || 0);
  return a + c;
}

/** Round a monetary value only at the EUR-cent boundary shared with the quote. */
export function roundEurCents(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

/**
 * Whole-party stay total for a CENT-PRECISE calendar per-pax price.
 *
 * The exact variant is only valid for a verified voucher redemption: the
 * calendar's visible top-up, receipt and quote must then be the same
 * whole-party residual. Ordinary bookings intentionally keep Calendar Option C
 * through {@link voucherStayTotal}; do not replace that caller accidentally.
 */
export function voucherExactStayTotal({
  perPaxPrice,
  activityPerPax,
  adults,
  children,
  hasActivity,
}) {
  const perPax = Math.max(0, Number(perPaxPrice) || 0);
  const hotelPax = hotelChargeablePax({ adults });
  const ticketPax = ticketChargeablePax({ adults, children, hasActivity });
  if (ticketPax === 0) return roundEurCents(hotelPax * perPax);
  // `Number(null)` and `Number("")` are 0, which would read an ABSENT ticket as
  // a known-free one and under-state the charge, so reject them by identity
  // before the numeric coercion.
  const ticketRaw =
    activityPerPax === null || activityPerPax === "" ? NaN : Number(activityPerPax);
  const ticketKnown = Number.isFinite(ticketRaw) && ticketRaw >= 0;
  if (!ticketKnown) {
    // Unknown ticket component: keep the old (over-stating) full-party shape.
    return roundEurCents(ticketPax * perPax);
  }
  const ticket = Math.min(ticketRaw, perPax);
  const hotelPerPax = perPax - ticket;
  return roundEurCents(hotelPerPax * hotelPax + ticket * ticketPax);
}

/**
 * Historical local fallback for an ordinary booking calendar price.
 *
 * Calendar Option C (Jeanne 2026-05-16) rounds the displayed per-pax amount
 * up before multiplying it into the local subtotal. It remains the rule for
 * non-voucher flows. A valid voucher follows the exact counterpart above so
 * its group residual cannot diverge from the receipt/quote.
 */
export function voucherStayTotal({ perPaxPrice, ...stay }) {
  const optionCPerPax = Math.ceil(Math.max(0, Number(perPaxPrice) || 0));
  return voucherExactStayTotal({ ...stay, perPaxPrice: optionCPerPax });
}

/**
 * Exact whole-party voucher residual for a calendar price.
 *
 * This is intentionally separate from {@link voucherStayTotal}: the stay
 * total and the voucher amount can each be cent-precise while their binary
 * subtraction still produces a float tail. Every customer-visible calendar
 * supplement therefore goes through this one EUR-cent boundary before it is
 * formatted or handed to a later booking surface.
 */
export function voucherSurchargeTotal({ voucherAmount, voucherPercentage = 0, ...stay }) {
  const stayTotal = voucherExactStayTotal(stay);
  const percentage = Math.max(0, Number(voucherPercentage) || 0);
  const voucher = percentage > 0
    ? stayTotal * (percentage / 100)
    : Math.max(0, Number(voucherAmount) || 0);
  return roundEurCents(Math.max(0, stayTotal - voucher));
}

/**
 * The per-person TICKET component of one `/api/booking/calendar` entry
 * (`activityPrice`, mapped from backend `activityPricePerPerson`), or
 * `undefined` when it is UNKNOWN. A missing, null, empty, zero, negative or
 * non-numeric value is unknown on purpose: `voucherStayTotal` then keeps the
 * old over-stating full-party shape instead of reading an absent ticket as a
 * free one (which would drop the children's tickets from the display).
 */
export function calendarTicketPerPax(entry) {
  const raw = entry?.activityPrice;
  if (raw === null || raw === undefined || raw === "") return undefined;
  const ticket = Number(raw);
  return Number.isFinite(ticket) && ticket > 0 ? ticket : undefined;
}

/**
 * Per-date ticket map for the SAME calendar rows a price map reads.
 * `pickEntry(losData)` must select exactly the entry the price comes from
 * (booking grid: `losData[losKey]`; offer calendar: `losData["0"]` or the
 * first entry) so price and ticket never come from two different rows.
 * `hasPrice(entry)` mirrors the price map's own "is this a priced row" test;
 * a date whose row is not priced gets no ticket either.
 */
export function ticketPerPaxByDate(calendarPrices, pickEntry, hasPrice) {
  const map = {};
  Object.entries(calendarPrices || {}).forEach(([date, losData]) => {
    if (!losData || typeof losData !== "object") return;
    const entry = pickEntry(losData);
    if (!entry || !hasPrice(entry)) return;
    const ticket = calendarTicketPerPax(entry);
    if (ticket !== undefined) map[date] = ticket;
  });
  return map;
}

/**
 * Highest ticket per LOS bucket, for the "vanaf EUR X" duration buttons.
 * Those buttons show a MIN over dates, so there is no single row to read the
 * ticket from. The HIGHEST ticket seen in the bucket keeps the label on the
 * over-stating side: a higher ticket moves more of the per-pax onto the
 * full-party side (total = perPax x adults + ticket x children), so the
 * "vanaf" can only be too high by the ticket-component difference, never
 * lower than the next screen.
 * Entry keys are LOS indexes into `losOptions`. `hasPrice(entry)` is the
 * same "is this a priced row" test the per-date map uses, so an unpriced or
 * unavailable row never contributes a ticket (reviewer-bot on fd3994c8);
 * omitted, every row counts.
 */
export function maxTicketPerPaxByLos(calendarPrices, losOptions, hasPrice = () => true) {
  const map = {};
  Object.values(calendarPrices || {}).forEach((losData) => {
    if (!losData || typeof losData !== "object") return;
    Object.entries(losData).forEach(([entryKey, entry]) => {
      const los = (losOptions || [])[Number(entryKey)];
      if (los === undefined || !entry || !hasPrice(entry)) return;
      const ticket = calendarTicketPerPax(entry);
      if (ticket === undefined) return;
      if (map[los] === undefined || ticket > map[los]) map[los] = ticket;
    });
  });
  return map;
}
